import { Space, Typography, theme } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

export type Step = {
  key: string;
  label: string;
  /** One line on what is there now — "3 sections", "not published yet". */
  detail: string;
  done: boolean;
};

type Props = {
  steps: Step[];
  activeKey: string;
  onSelect: (key: string) => void;
};

/**
 * Where this template has got to, and what is left.
 *
 * Deliberately not a wizard. A template is built once and then revisited for
 * years, so a linear "next, next, finish" is the wrong shape: every step here
 * stays reachable forever, in any order, and simply reports whether it has
 * anything in it yet. It guides someone doing this for the first time without
 * getting in the way of someone changing one setting on their tenth visit.
 */
const SetupProgress = ({ steps, activeKey, onSelect }: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 4,
        flexWrap: "wrap",
      }}
    >
      {steps.map((step, index) => {
        const active = step.key === activeKey;
        return (
          <div
            key={step.key}
            role="button"
            tabIndex={0}
            aria-current={active}
            onClick={() => onSelect(step.key)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(step.key);
              }
            }}
            style={{
              flex: "1 1 160px",
              minWidth: 150,
              padding: "8px 12px",
              cursor: "pointer",
              borderRadius: token.borderRadius,
              background: active ? token.controlItemBgActive : undefined,
              borderBottom: `2px solid ${
                active
                  ? token.colorPrimary
                  : step.done
                  ? token.colorSuccessBorder
                  : token.colorBorderSecondary
              }`,
            }}
          >
            <Space size={6}>
              {step.done ? (
                <CheckCircleFilled style={{ color: token.colorSuccess }} />
              ) : (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: `1px solid ${token.colorBorder}`,
                    color: token.colorTextTertiary,
                    fontSize: 10,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {index + 1}
                </span>
              )}
              <Text strong={active} style={{ fontSize: 13 }}>
                {t(step.label)}
              </Text>
            </Space>
            <div style={{ marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {step.detail}
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SetupProgress;
